// src/pages/PatientDetailPage.js
import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { Container, Typography, Box, Stack, Paper, Tabs, Tab } from "@mui/material";
import { motion } from "framer-motion";

import PatientUnifiedCardV2 from "./PatientMapComp/PatientDetails/PatientUnifiedCardV2";
import ClinicalContextPanel from "./PatientMapComp/PatientDetails/ClinicalContextPanel";
import AiReportsPanel from "./PatientMapComp/PatientDetails/AiReportsPanel";

import { ClinicalSummary } from "../ClinicalDataCollector/components/output";

import { AdvancedMdContext } from "../../context/AdvancedMD/AdvancedMdContext";
import { setAdvancedMdPatientId, selectPatient } from "../../store/Profiles/PatientMapSelectSlice";

import { buildHccClinicalNotesText } from "../../utils/clinicalNotesFormatter";

import { generateHccRafModelFromAI } from "../../utils/services/hccAiService";

import { InfoPat2Context } from "../../context/InfoPat2Context/InfoPat2Context";
import { NoRecentVisitContext } from "../../context/PatientContext/NoRecentVisitContext/NoRecentVisitContext";
import { CodingOpContext } from "../../context/CodingOpContext/CodingOpContext";
import { CareGapContext } from "../../context/PatientContext/CareGapContext/CareGapContext";
import PatientActionsBar from "./PatientMapComp/PatientDetails/PatientActionsBar";

// ===================== Helpers =====================
function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
}

function normalizeBool(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (v === 1 || v === "1" || v === "true" || v === "yes") return true;
  if (v === 0 || v === "0" || v === "false" || v === "no") return false;
  return null;
}

function splitFullName(fullName) {
  const s = String(fullName || "").trim().replace(/\s+/g, " ");
  if (!s) return { firstName: "", middleName: "", lastName: "" };
  const parts = s.split(" ");
  if (parts.length === 1) return { firstName: parts[0], middleName: "", lastName: "" };
  if (parts.length === 2) return { firstName: parts[0], middleName: "", lastName: parts[1] };
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

function formatYYYYMMDD(d) {
  if (!d) return "—";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function PatientDetailPage() {
  const dispatch = useDispatch();
  const location = useLocation();

  const [workspaceMode, setWorkspaceMode] = useState(0);
  const [notesLoadedFor, setNotesLoadedFor] = useState(null);

  // RAF AI
  const [rafCurrentConditions, setRafCurrentConditions] = useState([]);
  const [rafFutureConditions, setRafFutureConditions] = useState([]);
  const [rafAnalysisSummary, setRafAnalysisSummary] = useState("");
  const [rafLoading, setRafLoading] = useState(false);
  const [rafError, setRafError] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const [hccConditionsFromAi, setHccConditionsFromAi] = useState([]);

  // ===================== AdvancedMD Context =====================
  const {
    patientLookupResults,
    lookupLoading,
    lookupError,
    matchPatientInAdvancedMd,

    annualWellnessNotes,
    followUpExamNotes,
    telehealthNotes,
    hospitalDischargeNotes,

    loadAnnualWellnessNotes,
    loadFollowUpExamNotes,
    loadTelehealthNotes,
    loadHospitalDischargeNotes,
  } = useContext(AdvancedMdContext);

  // ===================== Membership + LastVisit contexts =====================
  const {
    membership,
    loading: membershipLoading,
    error: membershipError,
    loadPatientMembership,
  } = useContext(InfoPat2Context);

  const {
    fetchNoRecentVisitDetails,
    NoRecentVisitDetails,
    detailsLoading: lastVisitLoading,
  } = useContext(NoRecentVisitContext);

  // ===================== CodingOp Context =====================
  const {
    totalCount: codingOpsTotalCount,
    loadCodingOpportunitiesByPatient,
  } = useContext(CodingOpContext);

  // ===================== CareGap Context =====================
  const {
    careGapDetails,
    fetchCareGapDetails,
  } = useContext(CareGapContext);

  // ===================== Redux =====================
  const { PatientSelect, advancedMdPatientId } = useSelector((state) => state.patientMapSelect);

  // ===================== Hydrate PatientSelect from navigation state =====================
  useEffect(() => {
    const row = location?.state?.patientRow;
    if (!row || typeof row !== "object") return;

    const incomingId = row?.cipId ?? row?.CIP_ID ?? row?.memberId ?? row?.id;
    const currentId = PatientSelect?.cipId ?? PatientSelect?.CIP_ID ?? PatientSelect?.memberId ?? PatientSelect?.id;

    if (!currentId || (incomingId && String(incomingId) !== String(currentId))) {
      dispatch(selectPatient({ PatientSelect: row }));
    }
  }, [location, PatientSelect, dispatch]);

  // ===================== Resolve CIP ID robusto =====================
  const patientCipId = useMemo(() => {
    const v =
      PatientSelect?.cipId ??
      PatientSelect?.CIP_ID ??
      PatientSelect?.CipId ??
      PatientSelect?.cip_id ??
      null;

    const s = v ? String(v).trim() : "";
    return s || null;
  }, [PatientSelect]);

  // ===================== Load Membership by CIP =====================
  useEffect(() => {
    if (!patientCipId) return;
    loadPatientMembership(patientCipId);
  }, [patientCipId, loadPatientMembership]);

  // ===================== Load LastVisit by CIP =====================
  useEffect(() => {
    if (!patientCipId) return;
    fetchNoRecentVisitDetails(patientCipId);
  }, [patientCipId, fetchNoRecentVisitDetails]);

  // ===================== Load Coding Opportunities by CIP =====================
  useEffect(() => {
    if (!patientCipId) return;
    loadCodingOpportunitiesByPatient(patientCipId);
  }, [patientCipId, loadCodingOpportunitiesByPatient]);

  // ===================== Load Care Gaps by CIP =====================
  useEffect(() => {
    if (!patientCipId) return;
    fetchCareGapDetails(patientCipId);
  }, [patientCipId, fetchCareGapDetails]);

  const lastVisitRecord = useMemo(() => {
    const d = NoRecentVisitDetails;
    if (!d) return null;
    if (Array.isArray(d)) return d[0] ?? null;
    if (Array.isArray(d?.data)) return d.data[0] ?? null;
    return d;
  }, [NoRecentVisitDetails]);

  // ===================== display patient =====================
  const displayPatient = useMemo(() => {
    if (!PatientSelect || Object.keys(PatientSelect).length === 0) return null;

    const rosterFullName = PatientSelect.memberFullName || PatientSelect.fullName || "";
    const fromRoster = splitFullName(rosterFullName);

    const f = PatientSelect.firstName || fromRoster.firstName || "";
    const m = PatientSelect.middleName || fromRoster.middleName || "";
    const l = PatientSelect.lastName || fromRoster.lastName || "";

    const fullName = rosterFullName || [f, m, l].filter(Boolean).join(" ").trim();

    return {
      cipId: patientCipId,
      id: PatientSelect.memberId ?? PatientSelect.id ?? patientCipId ?? null,

      firstName: f,
      middleName: m,
      lastName: l,
      fullName,

      dob: PatientSelect.dob || PatientSelect.dateOfBirth || PatientSelect.birthDate || null,
      gender: PatientSelect.gender || PatientSelect.sex || "",
      sex: PatientSelect.sex || "",

      mrn: PatientSelect.chart || PatientSelect.chartId || PatientSelect.memberId || null,
      payer: PatientSelect.payer || PatientSelect.insurancePlan || "—",
      language: PatientSelect.language || "—",
    };
  }, [PatientSelect, patientCipId]);

  const patientAge = useMemo(() => calcAge(displayPatient?.dob), [displayPatient]);
  const searchName = useMemo(() => displayPatient?.fullName || "", [displayPatient]);

  // ===================== AdvancedMD match =====================
  const matchPayload = useMemo(() => {
    if (!displayPatient) return null;

    const firstName = displayPatient.firstName || "";
    const lastName = displayPatient.lastName || "";
    const dob = displayPatient.dob || null;
    const sex = displayPatient.gender || displayPatient.sex || "";

    if (!firstName && !lastName && !displayPatient.fullName) return null;

    let dobIso = null;
    if (dob) {
      const d = new Date(dob);
      if (!Number.isNaN(d.getTime())) dobIso = d.toISOString();
    }

    return { firstName, lastName, dob: dobIso, sex };
  }, [displayPatient]);

  useEffect(() => {
    if (!matchPayload) return;
    matchPatientInAdvancedMd(matchPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchPayload]);

  const bestMatch = useMemo(
    () => (patientLookupResults && patientLookupResults.length > 0 ? patientLookupResults[0] : null),
    [patientLookupResults]
  );

  const candidate = bestMatch?.candidate ?? null;

  const chartValue =
    candidate &&
    (candidate.chart ||
      candidate.Chart ||
      candidate.chartId ||
      candidate.ChartId ||
      candidate.ChartID ||
      candidate.chart_id);

  const scorePercent = useMemo(() => {
    if (!bestMatch) return null;

    const rawScore =
      bestMatch.score ??
      bestMatch.matchScore ??
      bestMatch.MatchScore ??
      bestMatch.probability ??
      bestMatch.Probability ??
      (candidate && candidate.score) ??
      null;

    if (rawScore === null || rawScore === undefined || rawScore === "") return null;

    const n = Number(rawScore);
    if (Number.isNaN(n)) return null;

    return n <= 1 ? Math.round(n * 100) : Math.round(n);
  }, [bestMatch, candidate]);

  const candidateDob = candidate && (candidate.dob || candidate.dateOfBirth || candidate.birthDate || candidate.DOB);
  const resolvedDob = candidateDob || displayPatient?.dob || "—";

  useEffect(() => {
    if (!candidate?.id) return;
    const rawId = String(candidate.id);
    const normalizedId = rawId.startsWith("pat") ? rawId.slice(3) : rawId;
    dispatch(setAdvancedMdPatientId(normalizedId));
  }, [candidate, dispatch]);

  // ===================== Central notes load =====================
  useEffect(() => {
    if (!advancedMdPatientId) return;
    if (notesLoadedFor === advancedMdPatientId) return;

    loadAnnualWellnessNotes(advancedMdPatientId);
    loadFollowUpExamNotes(advancedMdPatientId);
    loadTelehealthNotes(advancedMdPatientId);
    loadHospitalDischargeNotes(advancedMdPatientId);

    setNotesLoadedFor(advancedMdPatientId);
  }, [
    advancedMdPatientId,
    notesLoadedFor,
    loadAnnualWellnessNotes,
    loadFollowUpExamNotes,
    loadTelehealthNotes,
    loadHospitalDischargeNotes,
  ]);

  const clinicalNotesForHcc = useMemo(() => {
    return buildHccClinicalNotesText({
      annualWellnessNotes: annualWellnessNotes || [],
      followUpExamNotes: followUpExamNotes || [],
      telehealthNotes: telehealthNotes || [],
      hospitalDischargeNotes: hospitalDischargeNotes || [],
    });
  }, [annualWellnessNotes, followUpExamNotes, telehealthNotes, hospitalDischargeNotes]);

  // ===================== Handlers =====================
  const handleRetryMatch = useCallback(() => {
    if (!matchPayload) return;
    matchPatientInAdvancedMd(matchPayload);
  }, [matchPayload, matchPatientInAdvancedMd]);

  const handleGenerateRafWithAI = useCallback(async () => {
    if (!clinicalNotesForHcc || !clinicalNotesForHcc.trim()) return;

    setRafLoading(true);
    setRafError(null);
    setRafAnalysisSummary("");
    setRafCurrentConditions([]);
    setRafFutureConditions([]);

    try {
      const { currentConditions, futureConditions, analysisSummary } =
        await generateHccRafModelFromAI({ clinicalNotes: clinicalNotesForHcc });

      setRafCurrentConditions(currentConditions || []);
      setRafFutureConditions(futureConditions || []);
      setRafAnalysisSummary(analysisSummary || "");
    } catch (err) {
      console.error("[RAF] Error generating RAF model with AI:", err);
      setRafError("Error generating RAF scores with AI.");
    } finally {
      setRafLoading(false);
    }
  }, [clinicalNotesForHcc]);

  // ===================== Flags / Assignment =====================
  const patientFlags = useMemo(() => {
    return {
      isHighRisk: normalizeBool(PatientSelect?.isHighRisk),
      isHighCost: normalizeBool(PatientSelect?.isHighCost),
      isEmergency: normalizeBool(PatientSelect?.isEmergency),
      isAdmitted: normalizeBool(PatientSelect?.isAdmitted),
      isDischarged: normalizeBool(PatientSelect?.isDischarged),
      recentHospitalization: null,
    };
  }, [PatientSelect]);

  const patientAssignment = useMemo(() => {
    return {
      pcpName: PatientSelect?.pcpName || PatientSelect?.pcp || null,
      pcpTitle: PatientSelect?.pcpTitle || null,
      locationName:
        PatientSelect?.medicalCenter ||
        PatientSelect?.locationName ||
        PatientSelect?.location ||
        null,
      locationAddress: PatientSelect?.locationAddress || null,
      memberType: PatientSelect?.memberType || null,
      payerPlan: displayPatient?.payer || null,
    };
  }, [PatientSelect, displayPatient]);

  // ✅ Memberships mapping (TODOS los campos del SP)
  const membershipsPreview = useMemo(() => {
    if (!Array.isArray(membership)) return [];

    return membership.map((m) => ({
      // SP columns (usa ambas variantes por si tu API mapea distinto)
      memberType: m.VAR_MEMBER_TYPE ?? m.varMemberType ?? m.memberType ?? "",
      cipId: m.VAR_CIP_ID ?? m.varCipId ?? m.cipId ?? "",
      memberId: m.VAR_MEMBER_ID ?? m.varMemberId ?? m.memberId ?? "",
      medicareBeneficiaryId:
        m.VAR_MEDICARE_BENEFICIARY_ID ?? m.varMedicareBeneficiaryId ?? m.medicareBeneficiaryId ?? "",
      aNPREFIX: m.VAR_A_N_PREFIX ?? m.varANPrefix ?? m.aNPREFIX ?? "",
      memberPolicyId: m.VAR_MEMBER_POLICY_ID ?? m.varMemberPolicyId ?? m.memberPolicyId ?? "",
      networkCode: m.VAR_NETWORK_CODE ?? m.varNetworkCode ?? m.networkCode ?? "",
      networkName: m.VAR_NETWORK_NAME ?? m.varNetworkName ?? m.networkName ?? "",
      metalLevel: m.VAR_METAL_LEVEL ?? m.varMetalLevel ?? m.metalLevel ?? "",
      memberPlan: m.VAR_MEMBER_PLAN ?? m.varMemberPlan ?? m.memberPlan ?? "",
      memberPlanEffectiveDate:
        m.DAT_MEMBER_PLAN_EFFECTIVE_DATE ?? m.datMemberPlanEffectiveDate ?? m.memberPlanEffectiveDate ?? "",
      groupNumber: m.VAR_GROUP_NUMBER ?? m.varGroupNumber ?? m.groupNumber ?? "",

      addressLine1: m.VAR_ADDRESS_LINE_1 ?? m.varAddressLine1 ?? m.addressLine1 ?? "",
      addressLine2: m.VAR_ADDRESS_LINE_2 ?? m.varAddressLine2 ?? m.addressLine2 ?? "",
      cityName: m.VAR_CITY_NAME ?? m.varCityName ?? m.cityName ?? "",
      state: m.VAR_STATE ?? m.varState ?? m.state ?? "",
      zipCode: m.VAR_ZIP_CODE ?? m.varZipCode ?? m.zipCode ?? "",
      zip4: m.VAR_ZIP4 ?? m.varZip4 ?? m.zip4 ?? "",
      county: m.VAR_COUNTY ?? m.varCounty ?? m.county ?? "",

      idLocation: m.NUM_ID_LOCATION ?? m.numIdLocation ?? m.idLocation ?? "",
      medicalCenter: m.VAR_MEDICAL_CENTER ?? m.varMedicalCenter ?? m.medicalCenter ?? "",
      officeLocation: m.VAR_OFFICE_LOCATION ?? m.varOfficeLocation ?? m.officeLocation ?? "",

      // status: tu ejemplo dice 0 y lo quieres como ACTIVE
      statusRaw: m.NUM_STATUS ?? m.numStatus ?? m.status ?? 0,
      statusText: Number(m.NUM_STATUS ?? m.numStatus ?? m.status ?? 0) === 0 ? "ACTIVE" : "INACTIVE",

      // helpers extra (si quieres)
      effectiveDatePretty:
        (m.DAT_MEMBER_PLAN_EFFECTIVE_DATE || m.memberPlanEffectiveDate)
          ? formatYYYYMMDD(m.DAT_MEMBER_PLAN_EFFECTIVE_DATE || m.memberPlanEffectiveDate)
          : "—",
    }));
  }, [membership]);

  const membershipsMeta = useMemo(() => {
    const total = membershipsPreview.length;

    // active: si 0 es ACTIVE
    const activeCount = membershipsPreview.filter((x) => Number(x.statusRaw) === 0).length;

    return { total, activeCount };
  }, [membershipsPreview]);

  // ===================== Render =====================
  const WORKSPACE_OFFSET_PX = 170;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <Typography variant="h4" fontWeight={900} gutterBottom>
          Patient Detail
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Patient profile, clinical context, and AI-generated reports.
        </Typography>
      </motion.div>

      <PatientActionsBar
        displayPatient={displayPatient}
        advancedMdPatientId={advancedMdPatientId}
        addClinicalNotePath="/CreateClinicalNotePage"
        addCarePlanPath="/ai-careplan/new"
        quickActions={[
          { label: "Generate RAF", onClick: handleGenerateRafWithAI, disabled: !clinicalNotesForHcc?.trim() },
        ]}
      />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "380px 1fr" },
          height: { md: `calc(100vh - ${WORKSPACE_OFFSET_PX}px)` },
        }}
      >
        {/* LEFT */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, height: { md: "100%" }, overflowY: { md: "auto" } }}>
          <Stack spacing={2}>
            <PatientUnifiedCardV2
              displayPatient={displayPatient}
              patientAge={patientAge}
              lookupLoading={lookupLoading}
              lookupError={lookupError}
              candidate={candidate}
              bestMatch={bestMatch}
              scorePercent={scorePercent}
              resolvedDob={resolvedDob}
              chartValue={chartValue}
              searchName={searchName}
              matchPayload={matchPayload}
              onRetry={handleRetryMatch}
              flags={patientFlags}
              assignment={patientAssignment}
              membershipsPreview={membershipsPreview}
              membershipsMeta={membershipsMeta}
              onOpenMemberships={null}
              onOpenAdvancedMd={null}
              lastVisit={lastVisitRecord}
              lastVisitLoading={lastVisitLoading}
              codingOpsCount={codingOpsTotalCount}
              careGapsCount={Array.isArray(careGapDetails) ? careGapDetails.length : 0}
            />

            {membershipError ? (
              <Typography variant="caption" color="error">
                Membership error: {String(membershipError?.message || membershipError)}
              </Typography>
            ) : null}

            {membershipLoading ? (
              <Typography variant="caption" color="text.secondary">
                Loading memberships…
              </Typography>
            ) : null}
          </Stack>
        </Paper>

        {/* RIGHT */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, height: { md: "100%" }, overflowY: { md: "auto" } }}>
          <Stack spacing={2}>
            <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, p: 1 }}>
              <Tabs value={workspaceMode} onChange={(_, v) => setWorkspaceMode(v)} variant="fullWidth">
                <Tab label="Clinical Information" />
                <Tab label="AI Reports" />
              </Tabs>
            </Paper>

            {workspaceMode === 0 && (
              <ClinicalContextPanel
                contextStatus={{
                  label: clinicalNotesForHcc?.trim() ? "Notes ready" : "Notes missing",
                  color: clinicalNotesForHcc?.trim() ? "success" : "warning",
                  tooltip: "Based on merged AdvancedMD notes availability",
                  variant: "outlined",
                }}
              >
                <ClinicalSummary />
              </ClinicalContextPanel>
            )}

            {workspaceMode === 1 && (
              <AiReportsPanel
                displayPatient={displayPatient}
                patientAge={patientAge}
                advancedMdPatientId={advancedMdPatientId}
                annualWellnessNotes={annualWellnessNotes}
                followUpExamNotes={followUpExamNotes}
                telehealthNotes={telehealthNotes}
                hospitalDischargeNotes={hospitalDischargeNotes}
                clinicalNotesForHcc={clinicalNotesForHcc}
                rafLoading={rafLoading}
                rafError={rafError}
                rafAnalysisSummary={rafAnalysisSummary}
                rafCurrentConditions={rafCurrentConditions}
                rafFutureConditions={rafFutureConditions}
                onGenerateRaf={handleGenerateRafWithAI}
                onHccConditionsReady={setHccConditionsFromAi}
              />
            )}
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
